import { useEffect, useMemo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import styles from './Profile.module.css'

import LoginIcon from '../../../assets/login.svg?react'
import LoginWhite from '../../../assets/login-white.svg?react'
import ProfileUser from '../../../assets/profile-user.svg?react'
import License from '../../../assets/icon-license.svg?react'
import Logout from '../../../assets/logout.svg?react'
import Close from '../../../assets/close.svg?react'

import { API_URL, URL_WIKI } from '../../../const'
import { useAuthActions, useAuthUser } from 'use-eazy-auth'
import { PersonalImages } from '../../PersonalImages'
import { useUser } from '../../../hooks/user'

type Props = {
  isOpenProfile: boolean
  setIsOpenProfile: (value: boolean) => void
}

export default function Profile({ isOpenProfile, setIsOpenProfile }: Props) {
  const { t } = useTranslation()
  const { user } = useAuthUser()
  const { logout, updateUser } = useAuthActions()
  const { pathname, search } = useLocation()

  const { data: userData, refetch: refetchUser } = useUser()

  const close = useCallback(() => setIsOpenProfile(false), [setIsOpenProfile])

  const username = useMemo(() => {
    if (!user?.username) return ''
    return user.username.replace('mw--', ' ')
  }, [user?.username])

  useEffect(() => {
    if (!isOpenProfile) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prevOverflow
    }
  }, [isOpenProfile])

  useEffect(() => {
    if (!isOpenProfile) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpenProfile, close])

  useEffect(() => {
    if (!user || !userData) return
    if (user.wlm_email_verified !== userData.wlm_email_verified) {
      updateUser(userData)
    }
  }, [user, userData, updateUser])

  useEffect(() => {
    if (!isOpenProfile || !user) return

    const handleFocus = () => {
      refetchUser()
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [isOpenProfile, user, refetchUser])

  useEffect(() => {
    if (!isOpenProfile || !user) return

    const interval = window.setInterval(() => {
      refetchUser()
    }, 30000)

    return () => window.clearInterval(interval)
  }, [isOpenProfile, user, refetchUser])

  const [showLicenseModal, setShowLicenseModal] = useState(false)

  if (!isOpenProfile) return null

  return (
    <div
      className={styles.ProfileOverlay}
      role="dialog"
      aria-modal="true"
      onClick={close}
    >
      <div className={styles.ProfileModal} onClick={(e) => e.stopPropagation()}>
        <div className="w-100 h-100 d-flex justify-content-center position-relative">
          <div
            className={styles.ProfileContent}
            style={{
              width: 532,
              transition: 'width 0.3s ease-in-out',
            }}
          >
            <div
              className={
                user ? styles.ProfileContainerUser : styles.ProfileContainer
              }
            >
              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className={styles.Close}
                  onClick={close}
                  aria-label={t('CLOSE') ?? 'Close'}
                >
                  <Close />
                </button>
              </div>

              {user ? (
                <div>
                  <div className={styles.LoginEffettuato}>
                    {t('USR_BOX_MSG_LOGIN_OK')}
                  </div>

                  <div className="mt-3 d-flex align-items-center">
                    <div>
                      <ProfileUser />
                    </div>
                    <div className="ms-2">
                      <div className={styles.UserLabel}>
                        {t('USR_BOX_USERNAME')}:
                      </div>
                      <div className={styles.NomeUtente}>{username}</div>
                    </div>
                  </div>

                  <div className="mt-3">
                    <button
                      className={styles.LogoutButton}
                      onClick={logout}
                      type="button"
                    >
                      <Logout /> {t('GENERIC_LOGOUT')}
                    </button>

                    {!user.wlm_email_verified && (
                      <div
                        className="alert alert-danger mt-3"
                        role="alert"
                        style={{ fontSize: 14 }}
                      >
                        <span>{t('USR_BOX_MSG_MAIL_WARNING_1')}</span>
                        <br />
                        <span>{t('USR_BOX_MSG_MAIL_WARNING_2')}</span> (
                        <a
                          href="https://commons.wikimedia.org/wiki/Special:Preferences"
                          style={{ lineBreak: 'anywhere' }}
                          target="_blank"
                          rel="noreferrer"
                        >
                          https://commons.wikimedia.org/wiki/Special:Preferences
                        </a>
                        )
                        <br />
                        <span>{t('USR_BOX_MSG_MAIL_WARNING_3')}</span>
                      </div>
                    )}
                  </div>

                  {username && (
                    <div className="mt-3">
                      <PersonalImages rows={3} cols={5} />
                      <a
                        href={`${URL_WIKI}/Special:ListFiles/${username.replace(
                          ' ',
                          '_'
                        )}`}
                        className={styles.ButtonImagesProfile}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img width={16} src="/commons.png" alt="Commons" />{' '}
                        {t('USR_BOX_SWITCH_TO_COMMONS')}
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="mt-5 d-flex flex-column align-items-center justify-content-center">
                    <LoginIcon />
                    <div className={styles.TextLogin}>
                      {t('USR_BOX_LOGIN_HINT')}
                    </div>
                  </div>

                  <div className="mt-2">
                    <button
                      className={styles.LoginButton}
                      onClick={() => {
                        setShowLicenseModal(true)
                      }}
                      type="button"
                    >
                      <LoginWhite className="me-1" /> {t('LOGIN')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {showLicenseModal && (
        <div
          className={styles.ResponseOverlay}
          onClick={() => {
            setShowLicenseModal(false)
          }}
        >
          <div className={styles.ResponseContainer}>
            <div
              className="position-absolute pointer"
              style={{
                top: 20,
                right: 20,
              }}
            >
              <Close />
            </div>
            <div>
              <License />
            </div>
            <div className={styles.TextBeforeLogin}>
              {t('USR_BOX_CONDITION_TITLE')}
            </div>
            <div className={styles.License}>
              <input
                checked
                className="me-2"
                type="checkbox"
                onChange={() => {}}
              />
              {t('USR_BOX_CONDITION_TEXT')}
            </div>
            <div className="w-100">
              <button
                className={styles.ButtonAccetta}
                // disabled
                onClick={() => {
                  localStorage.setItem('redirectUrl', pathname + search)
                  window.location.href = `${API_URL}/oauth/oauth-login?redirect_uri=${window.location.href}`
                }}
              >
                {t('USR_BOX_CONDITION_ACCEPT_BUTTON')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
